import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';

const EarningsChart = ({ data, timeframe }) => {
  const { isDarkMode } = useTheme();

  const formatXAxisLabel = (tickItem) => {
    const date = new Date(tickItem);
    if (timeframe === '24h') {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-3 sm:p-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {new Date(label).toLocaleDateString('en-US', { 
              weekday: 'short', 
              month: 'short', 
              day: 'numeric' 
            })}
          </p>
          <div className="mt-2 space-y-1">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Earnings: <span className="text-green-600">${payload[0].value.toFixed(2)}</span>
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Games: {payload[0].payload.games}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-3 sm:p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Earnings Overview
        </h3>
        <div className="flex items-center space-x-2">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-primary-500 rounded-full mr-2"></div>
            <span className="text-sm text-gray-500 dark:text-gray-400">Earnings</span>
          </div>
        </div>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 5,  bottom: 5 }}>
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke={isDarkMode ? '#374151' : '#e5e7eb'}
            />
            <XAxis 
              dataKey="date"
              tickFormatter={formatXAxisLabel}
              tick={{ fontSize: 12, fill: isDarkMode ? '#9ca3af' : '#6b7280' }}
              stroke={isDarkMode ? '#4b5563' : '#d1d5db'}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: isDarkMode ? '#9ca3af' : '#6b7280' }}
              stroke={isDarkMode ? '#4b5563' : '#d1d5db'}
              tickFormatter={(value) => `$${value.toFixed(0)}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="earnings" 
              stroke="#3b82f6" 
              strokeWidth={3}
              dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2, fill: '#ffffff' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-3 gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            ${data.reduce((sum, item) => sum + item.earnings, 0).toFixed(2)}
            {/* {item.earnings} */}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Earned</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            ${(data.reduce((sum, item) => sum + item.earnings, 0) / data.length).toFixed(2)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Daily Average</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {data.reduce((sum, item) => sum + item.games, 0)}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Total Games</p>
        </div>
      </div>
    </div>
  );
};

export default EarningsChart;
